// import { PageWrapper } from '../../components/page-wrapper';

// export function ExcursionsList() {
//   return <PageWrapper>List</PageWrapper>;
// }

// export default ExcursionsList;
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router';

import { LoadableStatus } from '../../mobx-utils';
import { Hero, Modal, PageLoader, Section } from '@cfm-media/pwa-ui-components';
import { parseHtml } from '../../core/utils';
import { useStore } from '../../core/store';
import { useTranslation } from '../../core/utils/translations';

import { ActivityTeaser } from '../../components/activity-teaser';
import { ErrorPage } from '../../components/error-page';
import { LoadSpinner } from '../../components/loader';
import { MtsWidgetDisplay } from '../../components/mts-widget-display';
import { PageWrapper } from '../../components/page-wrapper';
import { urls } from '../../urls';
import { dateFormatter } from '../../utils/date-formatter';
import classNames from 'classnames';

export const ExcursionsList = observer(() => {
  const navigate = useNavigate();
  const { t } = useTranslation(['activities-page', 'error-pages']);
  const [displayForm, setDisplayForm] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);

  const [formConfig, setFormConfig] = useState({});
  const [excursions, setExcursions] = useState([]);

  const [selectedTeasers, setSelectedTeasers] = useState([]);

  const handleCategoryClick = (teaserSlug) => {
    setSelectedTeasers((prevSelectedTeasers) => {
      if (prevSelectedTeasers.includes(teaserSlug)) {
        return prevSelectedTeasers.filter((slug) => slug !== teaserSlug);
      } else {
        return [...prevSelectedTeasers, teaserSlug];
      }
    });
  };

  function getCategories(excursions) {
    const categories = [];
    excursions?.forEach((excursion) => {
      excursion?.labels?.forEach((category) => {
        if (!categories.includes(category)) {
          categories.push(category);
        }
      });
    });
    return categories;
  }

  const categories = getCategories(excursions);

  const { booking } = {
    ...useStore().BookingStore,
  };

  useEffect(
    () => {
      const totalPassengers = booking?.data?.travellers?.length;
      const bookingEmail = booking?.data?.client?.email;

      const dateParts = booking?.data?.startDate?.split('.') || [];

      setFormConfig({
        bookingNumber: booking?.data?.id,
        hotelName: booking?.data?.hotelName,
        hotelCode: booking?.data?.hotels[0]?.code,
        totalAdults: totalPassengers,
        customerEmail: bookingEmail,
        totalChildren: 0,
        totalInfants: 0,
        day: dateParts[0] || 1,
        month: dateParts[1] || 1,
        year: dateParts[2] || 2021,
      });

      const checkAvailableExcursions = async () => {
        if (
          booking.data?.flights?.find(
            (flight) =>
              flight.arrivalAirport === 'AYT' && booking.data?.hotels?.length,
          )
        ) {
          const regionCode = booking.data.hotels[0].code.substring(0, 2);

          const data = await fetchExcursions(regionCode);

          setExcursions(data);
        }
      };

      checkAvailableExcursions();
    },
    [booking.data],
    excursions,
  );

  const { create, destroy, modal } = useActivityModal();

  async function onSubmit(event, config) {
    event.preventDefault();

    const res = await submitBookingRequest(config);
    if (res.messageId) {
      setEmailSuccess(true);
    }
  }

  if (booking?.status === LoadableStatus.loading) {
    return (
      <PageLoader variant="absolute" info="Buchungsformular wird geladen..." />
    );
  }

  if (booking?.status === LoadableStatus.error) {
    return (
      <PageWrapper>
        <ErrorPage
          body={t('error-pages:activities-fetch-error-body-text')}
          buttonText={t('error-pages:activities-fetch-error-back-button-text')}
          icon={'icon-warning'}
          link={urls.home}
          title={t('error-pages:activities-fetch-error-title-text')}
        />
      </PageWrapper>
    );
  }

  // Redirect MTS supplier
  const supplier = booking?.data?.toJSON()?.activitySupplier;
  if (supplier === 'MTS') {
    navigate(urls.excursions.mts);
  }

  if (excursions?.length === 0) {
    return (
      <PageWrapper>
        <ErrorPage
          body={t('activities-page:no-activities-body-text')}
          buttonText={t('activities-page:no-activities-back-button-text')}
          icon={'icon-warning'}
          link={urls.home}
          title={t('activities-page:no-activities-title-text')}
        />
      </PageWrapper>
    );
  }

  return (
    <>
      <PageWrapper>
        <Hero
          image="public/assets/images/teasers/activities.jpg"
          className="!h-[250px] sm:!h-[400px]"
          headline="Ausflüge"
        />
        <Section>
          <div className="mb-3 flex-wrap">
            {categories?.map((name, index) => (
              <Category
                key={index}
                onClick={handleCategoryClick.bind(null, name)}
                title={name}
                selected={selectedTeasers.includes(name)}
              />
            ))}
          </div>

          {excursions
            ?.filter(
              (excursion) =>
                selectedTeasers.length === 0 ||
                excursion.labels.some((label) =>
                  selectedTeasers.includes(label),
                ),
            )
            .map((activity) => (
              <ActivityTeaser
                key={activity?.excursionCode}
                id={activity?.excursionCode}
                image={activity?.images}
                title={activity?.title}
                pickup={activity?.pickup}
                importantInformation={activity?.importantInformation}
                labels={activity?.labels}
                adultPrice={activity?.adultPrice}
                childMaxAge={activity?.childMaxAge}
                childPrice={activity?.childPrice}
                infantMaxAge={activity?.infantMaxAge}
                infantPrice={activity?.infantPrice}
                dateFrom={dateFormatter(activity?.dateFrom)}
                dateTo={dateFormatter(activity?.dateTo)}
                duration={activity?.duration}
                description={activity?.description}
                weekdays={activity?.days}
                flexibility={activity?.flexibility}
                inclusives={activity?.inclusives}
                highlights={activity?.highlights}
              />
            ))}
        </Section>
      </PageWrapper>
    </>
  );
});

async function fetchExcursions(regionCode) {
  try {
    const response = await fetch(`/rms/api/excursions/region/${regionCode}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();

    return data;
  } catch (error) {
    console.info('Error while fetching excursions');
    console.error(error);
    console.log(error);
  }
}

async function submitBookingRequest(config) {
  try {
    const response = await fetch(`${urls.api.bookings}/activities/book`, {
      body: JSON.stringify(config),
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
    });

    const payload = await response.json();
    return payload;
  } catch (error) {
    console.info('Error while requesting activity booking');
    console.error(error);
  }
}

function useActivityModal() {
  const defaultModal = {
    content: null,
    headline: null,
    isVisible: false,
    key: Date.now(),
  };

  const [modal, setModal] = useState(defaultModal);

  const create = (headline, content) =>
    setModal({ isVisible: true, headline, content, key: Date.now() });

  const destroy = setModal.bind(null, defaultModal);

  return { create, destroy, modal };
}

export function Category({ title, onClick, selected }) {
  return (
    <div onClick={onClick} className="mb-2 mr-2 inline-block">
      <div
        className={classNames(
          'flex cursor-pointer items-center rounded-3xl px-3 py-2 shadow',
          [selected ? 'bg-brand-1 text-white' : 'bg-[#e3f0f0] text-brand-1'],
        )}
      >
        <span className="text-[10px] font-semibold uppercase">{title}</span>
      </div>
    </div>
  );
}
